import { TWO_WIRE_MODE } from '@/features/operations/components/pages/Diagnostics/MapViewTwoWire/types'
import { ELegendName } from '@/features/operations/components/pages/MapView/components/LayersMenu/helper'

import floorApi from '@/api/admin/floorApi'
import stationDataConfigApi from '@/api/config/stationDataConfig'
import twoWireApi from '@/api/config/twoWireApi'
import { stationsApi } from '@/api/operations'
import diagnosticsApi from '@/api/operations/diagnosticsApi'
import {
  diagnosticStatus,
  DiagnosticStatusEnum,
  ExtendedStationDetail,
  HIGH_VOLTAGE,
  IDiagnosticMapViewState,
  initialState,
  DecoderLeaksData,
  RunDiagnosticsParams,
  VoltagesData,
  TwoWireTestData,
  InterfaceStationDetailState,
} from '@/store/slices/DiagnosticMapView/types'
import { RootState } from '@/store/stores'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import _, { size } from 'lodash'
import controllerApi from '@/api/operations/controllerApi'

/**
 * Global flag to block updates after stop
 */
let shouldStopDiagnostics = false

/**
 * Actions
 */
export const fetchStationMapOverlay = createAsyncThunk(
  'diagnosticMapView/fetchStationMapOverlay',
  async (interfaceId: number, { dispatch, rejectWithValue }) => {
    try {
      const [reqOverlay, reqStations, reqAllStations] = await Promise.all([
        stationsApi.getAll(interfaceId),
        stationDataConfigApi.getAll({
          PageSize: 0,
          interfaceId,
        }),
        stationDataConfigApi.getListWithDetail(interfaceId, {
          PageSize: 0,
          interfaceId,
        }),
      ])

      const mergedData: ExtendedStationDetail[] =
        reqAllStations?.data?.data?.map((overlayItem: any) => {
          const stationData = reqStations.data.data.find(
            (stationItem: { id: number }) => stationItem.id === overlayItem.id,
          )
          const stationDataOverlay = reqOverlay.data.find(
            (stationItem: { id: number }) => stationItem.id === overlayItem.id,
          )
          let stationDataFormatted = {
            ...overlayItem,
            ...stationData,
            ...stationDataOverlay,
            latitude: overlayItem.grsCoordinatedLatitude || 0,
            longitude: overlayItem.grsCoordinatedLongitude || 0,

            markerStationError: { ...stationData.marker },
            markerLeak: { ...stationData.marker },
            markerLowVoltage: { ...stationData.marker },

            isShowOnMap: true,
            diagnosingStatus: DiagnosticStatusEnum.Default,
            originalId: overlayItem.originalId,

            diagnosticStatus: { ...diagnosticStatus },
          }

          if (!stationDataFormatted.marker) {
            stationDataFormatted.marker = {
              id: 0,
              name: ELegendName.VALVE_OFF,
              imageUrl: '',
              s3Url: '',
            }
          }

          stationDataFormatted = {
            ...stationDataFormatted,
            markerStationError: { ...stationData.marker },
            markerLeak: { ...stationData.marker },
            markerLowVoltage: { ...stationData.marker },
          }

          return stationDataFormatted
        })

      if (mergedData) {
        const locationId = size(mergedData) > 0 ? mergedData[0].locationId : '0'
        if (locationId) {
          const { data } = await floorApi.getById(locationId + '')
          dispatch(diagnosticMapViewSlice.actions.setFloors(data))

          if (data) {
            const floorDefault = data.find(
              (item: { isDefault: boolean }) => item.isDefault,
            )
            if (floorDefault) {
              mergedData.map((item) => {
                item.isShowOnMap = item.floorId === floorDefault.id
                return item
              })

              dispatch(
                diagnosticMapViewSlice.actions.setFloorFilter({
                  locationId: locationId,
                  floorId: floorDefault.id,
                }),
              )
            }
          }
        }
      }
      //filter mergedData only get data has both long and lat
      return mergedData
      // return mergedData.filter((item) => item.grsCoordinatedLatitude && item.grsCoordinatedLongitude) || []
    } catch (e) {
      rejectWithValue([])
      return []
    }
  },
)

export const fetchStationByInterface = createAsyncThunk(
  'diagnosticMapView/fetchStationByInterface',
  async (interfaceId: number, { dispatch, rejectWithValue }) => {
    try {
      const stationData = await controllerApi.getAll({
        PageSize: 0,
        interfaceId,
      })
      return stationData.data.data as InterfaceStationDetailState[]
    } catch (e) {
      return rejectWithValue(e)
    }
  },
)

export const runAllStationDiagnostics = createAsyncThunk(
  'diagnosticMapView/runAllStationDiagnostics',
  async (
    { interfaceId, lastStationId, stationData }: RunDiagnosticsParams,
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      shouldStopDiagnostics = false

      const values: ReturnType<typeof getState> = getState()
      const diagnosticMapView = (values as RootState).diagnosticMapView
      const subInterfaces = (values as RootState).diagnostic.subInterfaces

      const subInterfaceMap = new Map(
        subInterfaces.map((sub) => [sub.interfaceId, sub]),
      )

      dispatch(setDiagnosingStationList(stationData))
      dispatch(
        diagnosticMapViewSlice.actions.updateBatchStations(
          stationData.map((station) => {
            const sub = subInterfaceMap.get(station.originalId)

            const isOffline =
              sub?.interfaceDetail?.state == 'Offline'
            console.log(`Station ${station.id} ${station.originalId} is ${isOffline ? 'Offline' : 'Online'}`)
            return {
              ...station,
              diagnosingStatus: isOffline
                ? DiagnosticStatusEnum.Offline
                : diagnosticMapView.stations.length === stationData.length
                ? DiagnosticStatusEnum.Pending
                : DiagnosticStatusEnum.Default,
            }
          }),
        ),
      )

      const availableStations =
        stationData?.filter((station) => {
          const sub = subInterfaceMap.get(station.originalId)

          return sub?.interfaceDetail?.state !== 'Offline'
        }) || []

      const allStationIds = availableStations.map((item) => item.id)

      const response: any[] = []
      const batchSize = 3
      const batches = []
      let currentLastStationId = lastStationId

      for (let i = 0; i < allStationIds.length; i += batchSize) {
        batches.push(allStationIds.slice(i, i + batchSize))
      }

      for (const batchStationIds of batches) {
        if (shouldStopDiagnostics) {
          return response
        }

        try {
          const processingStations = stationData
            ?.filter((station) => batchStationIds.includes(station.id))
            .map((station) => ({
              ...station,
              diagnosingStatus: DiagnosticStatusEnum.Running,
            }))

          dispatch(
            diagnosticMapViewSlice.actions.updateBatchStations(
              processingStations,
            ),
          )

          const { data: diagnosticData } = await diagnosticsApi.runDiagnostic({
            interfaceId,
            lastStationId: currentLastStationId,
            stationIds: batchStationIds,
          })

          if (shouldStopDiagnostics) {
            return response
          }

          dispatch(
            diagnosticMapViewSlice.actions.updateCompletedStations({
              completedStations: batchStationIds,
              diagnosticResults: diagnosticData,
            }),
          )

          if (diagnosticData && diagnosticData.length > 0) {
            currentLastStationId = Math.max(
              ...diagnosticData.map((item: any) => item.id),
            )
          }

          const batchResults = diagnosticData
            .map((item: any) => {
              const selectedStation = _.find(
                stationData,
                (d) => d.id === item.id,
              )

              const { error } = item

              if (error) {
                let { description, reason, action } = error
                if (description) {
                  description = description.replace(/(\r\n|\n|\r)/gm, '')
                }
                if (reason) {
                  reason = reason.replace(/(\r\n|\n|\r)/gm, '')
                }
                if (action) {
                  action = action.replace(/(\r\n|\n|\r)/gm, '')
                }
                item.error = {
                  ...error,
                  description,
                  reason,
                  action,
                }
              }

              if (selectedStation) {
                return {
                  ...selectedStation,
                  ...item,
                  diagnosticStatus: item,
                  diagnosingStatus: item.status,
                }
              }
              return null
            })
            .filter(Boolean)

          response.push(...batchResults)

          dispatch(
            diagnosticMapViewSlice.actions.updateBatchStations(batchResults),
          )
        } catch (batchError) {
          const failedStations = stationData
            ?.filter((station) => batchStationIds.includes(station.id))
            .map((station) => ({
              ...station,
              diagnosingStatus: DiagnosticStatusEnum.Error,
              diagnosticStatus: {
                ...station.diagnosticStatus,
                error: {
                  ...station.diagnosticStatus.error,
                },
              },
            }))
          dispatch(
            diagnosticMapViewSlice.actions.updateBatchStations(failedStations),
          )
          console.error('Batch error:', batchError)
        }
      }

      return response
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchTwoWireTestData = createAsyncThunk(
  'diagnosticMapView/fetchTwoWireTestData',
  async (interfaceId: string, { rejectWithValue }) => {
    try {
      const { data } = await twoWireApi.getTwoWireTest(interfaceId)
      return data as TwoWireTestData
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchVoltagesList = createAsyncThunk(
  'diagnosticMapView/fetchVoltagesList',
  async (interfaceId: number, { rejectWithValue }) => {
    try {
      const { data } = await twoWireApi.getListVoltages(interfaceId.toString())
      return data as VoltagesData
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.Message ||
        error?.message ||
        'Failed to fetch voltages data'
      return rejectWithValue(errorMessage)
    }
  },
)

export const fetchLeaksList = createAsyncThunk(
  'diagnosticMapView/fetchLeaksList',
  async (interfaceId: string, { rejectWithValue }) => {
    try {
      const { data } = await twoWireApi.getListLeaks(interfaceId)
      return data as DecoderLeaksData[]
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.Message ||
        error?.message ||
        'Failed to fetch leaks data'
      return rejectWithValue(errorMessage)
    }
  },
)

export const resetDiagnostics = createAsyncThunk(
  'diagnosticMapView/resetDiagnostics',
  async (
    { interfaceId, lastStationId, stationData }: RunDiagnosticsParams,
    { dispatch },
  ) => {
    try {
      const allStationIds = stationData?.map((item) => item.id) || []

      const res = await diagnosticsApi.stopDiagnostic({
        interfaceId,
        lastStationId,
        stationIds: allStationIds,
      })

      const resetStations =
        stationData?.map((station) => ({
          ...station,
          diagnosingStatus: DiagnosticStatusEnum.Default,
          diagnosticStatus: diagnosticStatus,
        })) || []

      dispatch(
        diagnosticMapViewSlice.actions.updateBatchStations(resetStations),
      )

      dispatch(diagnosticMapViewSlice.actions.resetDiagnosticProgress())

      return res.data
    } catch (error) {
      console.error('Lỗi khi reset diagnostics:', error)
      return false
    }
  },
)

export const resetDiagnosticsPartially = createAsyncThunk(
  'diagnosticMapView/resetDiagnosticsPartially',
  async ({ stationData }: RunDiagnosticsParams, { dispatch }) => {
    try {
      const resetStations =
        stationData?.map((station) => ({
          ...station,
          diagnosingStatus: DiagnosticStatusEnum.Default,
          diagnosticStatus: diagnosticStatus,
        })) || []

      dispatch(
        diagnosticMapViewSlice.actions.updateBatchStations(resetStations),
      )

      dispatch(diagnosticMapViewSlice.actions.resetDiagnosticProgress())

      return true
    } catch (error) {
      console.error('Lỗi khi reset diagnostics:', error)
      return false
    }
  },
)

export const stopAllStationDiagnostics = createAsyncThunk(
  'diagnosticMapView/stopAllStationDiagnostics',
  async (params: RunDiagnosticsParams, { dispatch }) => {
    shouldStopDiagnostics = true
    dispatch(resetDiagnostics(params))
    return false
  },
)

export const diagnosticMapViewSlice = createSlice({
  name: 'diagnosticMapView',
  initialState,
  reducers: {
    resetDiagnosticProgress: (state) => {
      state.diagnosticProgress = {
        total: state.stations.length,
        completed: 0,
        completedStations: [],
      }
    },
    updateCompletedStations: (
      state,
      action: PayloadAction<{
        completedStations: number[]
        diagnosticResults: any[]
      }>,
    ) => {
      if (shouldStopDiagnostics) {
        return
      }
      const { completedStations, diagnosticResults } = action.payload
      state.diagnosticProgress.completed += completedStations.length
      state.diagnosticProgress.completedStations = [
        ...state.diagnosticProgress.completedStations,
        ...completedStations,
      ]
      if (state.diagnosticProgress.completed > state.stations.length) {
        state.diagnosticProgress.completed = state.stations.length
        state.diagnosticProgress.completedStations =
          state.diagnosticProgress.completedStations.slice(
            0,
            state.stations.length,
          )
      }

      completedStations.forEach((stationId) => {
        const stationIndex = state.stations.findIndex((s) => s.id === stationId)
        if (stationIndex !== -1) {
          const result = diagnosticResults.find((r) => r.id === stationId)
          state.stations[stationIndex] = {
            ...state.stations[stationIndex],
            diagnosingStatus: DiagnosticStatusEnum.Completed,
          }
        }
      })
    },

    setData: (state, action: PayloadAction<IDiagnosticMapViewState>) => {
      state.stations = action.payload.stations
      state.diagnosticsStatus = action.payload.diagnosticsStatus
    },
    setStationList: (state, action: PayloadAction<any[]>) => {
      state.stations = action.payload
      if (action.payload.length > 0)
        state.map.center = {
          latitude: action.payload[0].grsCoordinatedLatitude,
          longitude: action.payload[0].grsCoordinatedLongitude,
        }
    },
    setDiagnosingStationList: (state, action: PayloadAction<any[]>) => {
      state.diagnosingStations = action.payload
    },
    setFloors: (state, action: PayloadAction<any[]>) => {
      state.floors = action.payload.filter(
        (floor) => floor.name !== 'Ground Floor',
      )
    },
    setFloorFilter: (state, action: PayloadAction<any>) => {
      const { locationId, floorId } = action.payload
      if (floorId) state.filter.floorId = floorId
      if (locationId) state.filter.locationId = locationId
    },
    setMapTwoWireMode: (state, action: PayloadAction<TWO_WIRE_MODE>) => {
      const previousMode = state.mapTwoWireMode
      state.mapTwoWireMode = action.payload
      if (previousMode === action.payload) {
        return
      }
    },
    updateBatchStations: (
      state,
      action: PayloadAction<ExtendedStationDetail[]>,
    ) => {
      if (shouldStopDiagnostics) {
        return
      }
      action.payload.forEach((newStation) => {
        const index = state.stations.findIndex(
          (station) => station.id === newStation.id,
        )

        const getMarkerName = (status: string) => {
          switch (status) {
            case DiagnosticStatusEnum.Normal:
              return 'Valve On'
            case DiagnosticStatusEnum.Error:
              return 'Valve Error'
            case DiagnosticStatusEnum.Running:
              return 'Valve OFF Loading'
            case DiagnosticStatusEnum.Pending:
              // return 'Valve OFF Loading'
              return 'Valve OFF Loading'
            default:
              return 'Valve Off'
          }
        }

        if (index !== -1) {
          const updatedMarker = { ...state.stations[index].marker }

          const markerName = getMarkerName(
            newStation.diagnosticStatus.status || newStation.diagnosingStatus,
          )

          state.stations[index] = {
            ...state.stations[index],
            ...newStation,
            marker: {
              ...state.stations[index].marker,
              name: markerName,
            },
            markerStationError: {
              ...state.stations[index].markerStationError,
              name: markerName,
            },
          }
        } else {
          state.stations.push(newStation)
        }
      })
    },
    setStationsByFloor: (
      state,
      action: PayloadAction<{ locationId: number; floorId: number }>,
    ) => {
      const { locationId, floorId } = action.payload

      state.filter = { locationId, floorId }

      state.stations.forEach((station) => {
        station.isShowOnMap =
          station.locationId === locationId && station.floorId === floorId
      })
    },
    setMapZoom: (state, action: PayloadAction<number | null>) => {
      state.map.zoom = action.payload
    },
    setSubInterfaceForMapView: (state, action) => {
      state.selectedSubInterfaceForMapView = action.payload
    },
  },
  extraReducers: (builder) => {
    builder

      // fetch stationMapOverlay
      .addCase(fetchStationMapOverlay.pending, (state) => {
        state.loading = true
        state.isLoadingStationError = true
        state.isLoadingLeaks = true
        state.isLoadingLowVoltage = true
        state.error = null
      })
      .addCase(fetchStationMapOverlay.fulfilled, (state, action) => {
        state.loading = false
        state.isLoadingStationError = false
        state.isLoadingLeaks = false
        state.isLoadingLowVoltage = false
        const result = action.payload?.filter(
          (item) => item.address !== 0 && item?.index !== 0,
        )
        state.stations = result
        state.diagnosticProgress.total = result.length
        state.diagnosticProgress.completed = 0
        state.diagnosticProgress.completedStations = []

        if (state.stations.length > 0) {
          state.map.center.latitude = state.stations[0].latitude
          state.map.center.longitude = state.stations[0].longitude
          /**
           * @todo:
           * check with backend. Sometime backend return ELegendName.VALVE_WAITING instead of VALVE_OFF
           * so here we need to reset all to VALVE_OFF
           */
          state.stations.forEach((station) => {
            station.marker.name = ELegendName.VALVE_OFF
            station.markerStationError.name = ELegendName.VALVE_OFF
            station.markerLeak.name = ELegendName.VALVE_OFF
            station.markerLowVoltage.name = ELegendName.VALVE_OFF
          })
        }
      })
      .addCase(fetchStationMapOverlay.rejected, (state, action) => {
        state.loading = false
        state.isLoadingStationError = false
        state.isLoadingLeaks = false
        state.isLoadingLowVoltage = false
      })

      // fetchStationByInterface
      .addCase(fetchStationByInterface.pending, (state) => {
        state.isLoadingStationByInterface = true
      })
      .addCase(fetchStationByInterface.fulfilled, (state, action) => {
        const interfaceId = Number(action.meta.arg)
        state.isLoadingStationByInterface = false
        if (action.payload) {
          const grouped: Record<number, any[]> = {}

          action.payload.forEach((item) => {
            const key = item.twiOriginalId

            if (key === undefined || key === null) return

            if (!grouped[key]) {
              grouped[key] = []
            }

            grouped[key].push(item)
          })

          state.stationsByInterface = grouped
        }
        state.isSupController = true
      })
      .addCase(fetchStationByInterface.rejected, (state) => {
        state.isLoadingStationByInterface = false
      })

      // fetchStationMapOverlay
      .addCase(runAllStationDiagnostics.pending, (state, action) => {
        state.loading = true
        state.isLoadingStationError = true
        state.isLoadingLeaks = true
        state.isLoadingLowVoltage = true
        state.error = null

        const stationIds =
          action.meta.arg.stationData?.map((station: any) => station.id) || []

        state.stations = state.stations.map((station) => {
          if (stationIds.includes(station.id)) {
            if (station.marker) {
              station.marker.name = ELegendName.VALVE_OFF_LOADING
            }
            if (station.markerStationError) {
              station.markerStationError.name = ELegendName.VALVE_OFF_LOADING
            }
          }
          return station
        })
      })
      .addCase(
        runAllStationDiagnostics.fulfilled,
        (state, action: PayloadAction<ExtendedStationDetail[]>) => {
          if (shouldStopDiagnostics) {
            return
          }
          state.loading = false
          state.isLoadingStationError = false
          state.isLoadingLeaks = false
          state.isLoadingLowVoltage = false

          action.payload.map((st) => {
            const idx = state.stations.findIndex(
              (station) => station.id === st.id,
            )
            if (idx !== -1) {
              const existingStation = state.stations[idx]
              const existingMarkerName = state.stations[idx].marker?.name

              state.stations[idx] = {
                ...st,
                marker: {
                  ...st.marker,
                  name: existingMarkerName,
                },
                markerStationError: existingStation.markerStationError,
                markerLeak: existingStation.markerLeak,
                markerLowVoltage: existingStation.markerLowVoltage,
              }
            } else {
              state.stations.push(st)
            }
            return st
          })
        },
      )
      .addCase(runAllStationDiagnostics.rejected, (state, action) => {
        state.loading = false
        state.isLoadingStationError = false
        state.isLoadingLeaks = false
        state.isLoadingLowVoltage = false
        state.error = action.payload as string
      })

      // twoWireTest
      .addCase(fetchTwoWireTestData.pending, (state, action) => {
        const interfaceId = Number(action.meta.arg)
        const ifaceState = ensureInterfaceTwoWireTestState(state, interfaceId)
        ifaceState.isLoading = true
      })
      .addCase(fetchTwoWireTestData.fulfilled, (state, action) => {
        const interfaceId = Number(action.meta.arg)

        const ifaceState = ensureInterfaceTwoWireTestState(state, interfaceId)
        ifaceState.isLoading = false
        ifaceState.isTwoWireTestError = false
        ifaceState.twoWireTestData = action.payload
      })
      .addCase(fetchTwoWireTestData.rejected, (state, action) => {
        const interfaceId = Number(action.meta.arg)

        const ifaceState = ensureInterfaceTwoWireTestState(state, interfaceId)
        ifaceState.isLoading = false
        ifaceState.isTwoWireTestError = true
        state.error = action.payload as string
      })

      // LowVoltageDecoders
      .addCase(fetchVoltagesList.pending, (state, action) => {
        const interfaceId = Number(action.meta.arg)
        const ifaceState = ensureInterfaceVoltageState(state, interfaceId)
        ifaceState.isLoading = true
        ifaceState.getVoltageStateCompleted = false
        state.loading = true
        state.isLoadingLowVoltage = true
        state.error = null
        const interfaceStations = state.stationsByInterface[interfaceId] ?? []
        state.stations = state.stations.map((station) => {
          const stationInterface = interfaceStations.find(
            (si) =>
              si.index === station.index && si.address === station.address,
          )
          if (!stationInterface && state.isSupController) {
            return station
          }
          if (station.marker)
            station.marker.name = ELegendName.VALVE_OFF_LOADING
          if (station.markerLowVoltage)
            station.markerLowVoltage.name = ELegendName.VALVE_OFF_LOADING
          return station
        })
      })
      .addCase(fetchVoltagesList.fulfilled, (state, action) => {
        const interfaceId = Number(action.meta.arg)

        const ifaceState = ensureInterfaceVoltageState(state, interfaceId)

        state.loading = false
        state.isLoadingLowVoltage = false
        ifaceState.isLoading = false

        ifaceState.lowVoltageDecoders = action.payload
        state.lowVoltageDecoders = action.payload
        const interfaceStations = state.stationsByInterface[interfaceId] ?? []
        ifaceState.isVoltageStateError = false
        ifaceState.getVoltageStateCompleted = true
        state.stations = state.stations.map((station) => {
          const stationInterface = interfaceStations.find(
            (si) =>
              si.index === station.index && si.address === station.address,
          )

          if (!stationInterface && state.isSupController) {
            return station
          }
          const matchingVoltage = action.payload.find(
            (v) => v.addr === station.address,
          )

          if (matchingVoltage) {
            station.voltage = matchingVoltage
            if (matchingVoltage.voltage > HIGH_VOLTAGE) {
              if (station.marker) station.marker.name = ELegendName.VALVE_ON
              if (station.markerLowVoltage)
                station.markerLowVoltage.name = ELegendName.VALVE_ON
            } else {
              if (station.marker) station.marker.name = ELegendName.VALVE_ERROR
              if (station.markerLowVoltage)
                station.markerLowVoltage.name = ELegendName.VALVE_ERROR
            }
          } else {
            /**
             * @desc:
             * why set marker to green to all others station !!!
             */
            station.voltage = undefined
            if (station.marker) station.marker.name = ELegendName.VALVE_ON
            if (station.markerLowVoltage)
              station.markerLowVoltage.name = ELegendName.VALVE_ON
          }
          return station
        })
        console.log({ state, action }, 'fetchVoltagesList2')
      })
      .addCase(fetchVoltagesList.rejected, (state, action) => {
        const interfaceId = Number(action.meta.arg)

        const ifaceState = ensureInterfaceVoltageState(state, interfaceId)
        ifaceState.isLoading = false
        ifaceState.isVoltageStateError = true
        state.loading = false
        state.isLoadingLowVoltage = false
        state.error = action.payload as string
        // Reset markers to VALVE_OFF (gray) when API fails
        state.stations.forEach((station) => {
          if (station.marker) {
            station.marker.name = ELegendName.VALVE_OFF
          }
          if (station.markerLowVoltage) {
            station.markerLowVoltage.name = ELegendName.VALVE_OFF
          }
        })
      })

      // fetchLeaksList
      .addCase(fetchLeaksList.pending, (state, action) => {
        const interfaceId = Number(action.meta.arg)
        const ifaceState = ensureInterfaceLeakState(state, interfaceId)
        ifaceState.isLoading = true
        ifaceState.getLeakStateCompleted = false
        ifaceState.isLeakStateError = false
        state.isLoadingLeaks = true
        state.loading = true
        state.error = null
        const interfaceStations = state.stationsByInterface[interfaceId] ?? []
        state.stations = state.stations.map((station) => {
          const stationInterface = interfaceStations.find(
            (si) =>
              si.index === station.index && si.address === station.address,
          )

          if (!stationInterface && state.isSupController) {
            return station
          }
          if (station.marker) {
            station.marker.name = ELegendName.VALVE_OFF_LOADING
          }
          if (station.markerLeak) {
            station.markerLeak.name = ELegendName.VALVE_OFF_LOADING
          }
          return station
        })
      })
      .addCase(fetchLeaksList.fulfilled, (state, action) => {
        const interfaceId = Number(action.meta.arg)

        const ifaceState = ensureInterfaceLeakState(state, interfaceId)
        ifaceState.isLoading = false
        ifaceState.leaksData = action.payload
        ifaceState.lastLeakScanTime = new Date().toISOString()
        ifaceState.decoderLeakStatus = {}
        ifaceState.getLeakStateCompleted = true
        state.loading = false
        state.isLoadingLeaks = false

        state.leaksData = action.payload
        state.lastLeakScanTime = new Date().toISOString()
        state.decoderLeakStatus = {}

        const leakDecodersMap = new Map(
          action.payload?.map((leak) => [leak.addr, leak]) || [],
        )
        const interfaceStations = state.stationsByInterface[interfaceId] ?? []
        state.stations.forEach((station) => {
          const stationInterface = interfaceStations.find(
            (si) =>
              si.index === station.index && si.address === station.address,
          )

          if (!stationInterface && state.isSupController) {
            return station
          }
          const leakData = station.address
            ? leakDecodersMap.get(station.address)
            : undefined

          if (leakData) {
            if (station.marker) {
              station.marker.name = ELegendName.VALVE_ERROR
            }

            if (station.markerLeak) {
              station.markerLeak.name = ELegendName.VALVE_ERROR
            }

            station.decoderLeakStatus = {
              leakData,
            }

            state.decoderLeakStatus[station.id] = {
              hasLeak: true,
              leakData: {
                ...leakData,
              },
            }
          } else if (state.lastLeakScanTime) {
            if (station.marker) {
              station.marker.name = ELegendName.VALVE_ON
            }
            if (station.markerLeak) {
              station.markerLeak.name = ELegendName.VALVE_ON
            }
          } else {
            if (station.marker) {
              station.marker.name = ELegendName.VALVE_OFF
            }
            if (station.markerLeak) {
              station.markerLeak.name = ELegendName.VALVE_OFF
            }
          }
        })

        state.isLoadingLeaks = false
        state.loading = false
        state.error = null
      })
      .addCase(fetchLeaksList.rejected, (state, action) => {
        const interfaceId = Number(action.meta.arg)

        const ifaceState = ensureInterfaceLeakState(state, interfaceId)

        ifaceState.isLoading = false
        ifaceState.isLeakStateError = true
        state.isLoadingLeaks = false
        state.loading = false
        state.error = action.payload as string
        // Reset markers to VALVE_OFF (gray) when API fails
        state.stations.forEach((station) => {
          if (station.marker) {
            station.marker.name = ELegendName.VALVE_OFF
          }
          if (station.markerLeak) {
            station.markerLeak.name = ELegendName.VALVE_OFF
          }
        })
      })
      .addCase(stopAllStationDiagnostics.fulfilled, (state, action) => {
        state.stations = state.stations.map((station) => ({
          ...station,
          diagnosingStatus: DiagnosticStatusEnum.Default,
          diagnosticStatus: diagnosticStatus,
        }))
      })
  },
})

const ensureInterfaceLeakState = (
  state: IDiagnosticMapViewState,
  interfaceId: number,
) => {
  if (!state.leaksByInterface[interfaceId]) {
    state.leaksByInterface[interfaceId] = {
      isLoading: false,
      leaksData: [],
      decoderLeakStatus: {},
      stations: [],
      getLeakStateCompleted: false,
      isLeakStateError: false,
    }
  }

  return state.leaksByInterface[interfaceId]
}

const ensureInterfaceVoltageState = (
  state: IDiagnosticMapViewState,
  interfaceId: number,
) => {
  if (!state.voltagesByInterface[interfaceId]) {
    state.voltagesByInterface[interfaceId] = {
      isLoading: false,
      lowVoltageDecoders: [],
      stations: [],
      isVoltageStateError: false,
      getVoltageStateCompleted: false,
    }
  }

  return state.voltagesByInterface[interfaceId]
}

const ensureInterfaceTwoWireTestState = (
  state: IDiagnosticMapViewState,
  interfaceId: number,
) => {
  if (!state.twoWireTestsByInterface[interfaceId]) {
    state.twoWireTestsByInterface[interfaceId] = {
      isLoading: false,
      isTwoWireTestError: false
    }
  }
  return state.twoWireTestsByInterface[interfaceId]
}

export const { setData, setStationList, setDiagnosingStationList } =
  diagnosticMapViewSlice.actions

export const diagnosticMapViewState = (state: RootState) =>
  state.diagnosticMapView

export default diagnosticMapViewSlice.reducer
